<template>
<div class="pc-root" :style="themePageStyle">
  <nav class="pc-topbar"><div class="container d-flex align-items-center justify-content-between py-2">
    <div class="d-flex align-items-center" style="gap:12px;">
      <back-button class="me-2" />
      <a href="javascript:void(0)" @click="$router.push('/dashboard')"><img src="/assets/images/flockr_logo.png" alt="Flockr" style="height:34px;" /></a>
      <span class="fw-semibold" style="color:var(--fm-h,#F5F0E8);font-size:15px;">Payment Calculations <span v-if="farm.name" style="opacity:.7;font-weight:400;">· {{ farm.name }}</span></span>
    </div>
    <div class="d-flex gap-1">
      <button class="btn btn-sm btn-outline-warning" @click="manageBank=true"><i class="mdi mdi-bank me-1"></i>Bank accounts</button>
      <button v-if="!editing" class="btn btn-sm btn-warning" @click="openCreate"><i class="mdi mdi-plus me-1"></i>New calculation</button>
    </div>
  </div></nav>

  <div class="container pc-container">
    <div v-if="loading" class="text-center py-5"><div class="spinner-border text-warning"></div></div>

    <!-- LIST -->
    <div v-else-if="!editing">
      <div v-if="!calcs.length" class="pc-card text-center" style="padding:40px;">
        <i class="mdi mdi-cash-register" style="font-size:48px;color:var(--fm-s,#6B6356);"></i>
        <div style="color:var(--fm-h,#F5F0E8);font-weight:600;margin-top:8px;">No payment calculations yet</div>
        <div style="color:var(--fm-s,#6B6356);font-size:12px;margin-top:4px;">Create one after a slaughter sheet is signed off.</div>
        <button class="btn btn-warning mt-3" @click="openCreate"><i class="mdi mdi-plus me-1"></i>New calculation</button>
      </div>
      <div v-else>
        <div v-for="c in calcs" :key="'pc-'+c.id" class="pc-card mb-2">
          <div class="d-flex justify-content-between align-items-start flex-wrap" style="gap:8px;">
            <div class="flex-fill" style="min-width:0;">
              <div class="d-flex align-items-center gap-2 flex-wrap">
                <span class="pc-ref">{{ c.document_ref }}</span>
                <span class="pc-status" :class="'st-'+c.status">{{ statusLabel(c.status) }}</span>
              </div>
              <div style="color:var(--fm-h,#F5F0E8);font-weight:600;margin-top:4px;">KES {{ Number(c.grand_total_kes||0).toLocaleString() }}<span v-if="c.buyer_name" style="font-weight:400;color:var(--fm-s,#6B6356);"> · to {{ c.buyer_name }}</span></div>
              <div style="color:var(--fm-s,#6B6356);font-size:12px;margin-top:2px;">
                <i class="mdi mdi-calendar me-1"></i>{{ fD(c.calc_date) }}
                <span v-if="c.slaughter_sheet_ref"> · SS <strong>{{ c.slaughter_sheet_ref }}</strong></span>
                <span v-if="c.total_cold_cw_kg"> · {{ c.total_cold_cw_kg }} kg cold CW</span>
              </div>
            </div>
            <div class="d-flex gap-1 flex-wrap">
              <button class="btn btn-sm btn-warning" @click="openPrint(c)"><i class="mdi mdi-printer me-1"></i>View / Print</button>
              <button v-if="c.status !== 'paid'" class="btn btn-sm btn-outline-warning" @click="openEdit(c)"><i class="mdi mdi-pencil"></i></button>
              <button v-if="c.status === 'calculated' && isOwner" class="btn btn-sm btn-outline-success" @click="approve(c)"><i class="mdi mdi-check"></i></button>
              <button v-if="c.status === 'approved' && isOwner" class="btn btn-sm btn-success" @click="markPaid(c)"><i class="mdi mdi-cash-check me-1"></i>Mark paid</button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- EDIT FORM -->
    <div v-else>
      <div class="pc-card">
        <h5 class="fw-bold mb-3" style="color:var(--fm-h,#F5F0E8);"><i class="mdi mdi-calculator me-1"></i>{{ form.id ? 'Edit payment calculation' : 'New payment calculation' }}</h5>

        <div class="row g-2 mb-2">
          <div class="col-md-5"><label class="pc-label">Slaughter sheet (optional)</label>
            <select v-model.number="form.slaughterSheetId" class="pc-input w-100" @change="onSheetPicked">
              <option :value="0">— None —</option>
              <option v-for="s in signedSheets" :key="'sh-'+s.id" :value="s.id">{{ s.document_ref }} · {{ s.abattoir_name }} · {{ s.items_count }} animals</option>
            </select>
          </div>
          <div class="col-md-4"><label class="pc-label">Buyer / payee</label><input v-model="form.buyerName" class="pc-input w-100" placeholder="Choice Meat Ltd" /></div>
          <div class="col-md-3"><label class="pc-label">Calc date *</label><input v-model="form.calcDate" type="date" class="pc-input w-100" /></div>
        </div>

        <h6 class="fw-bold mt-3 mb-2" style="color:#BFA164;">Carcass proceeds</h6>
        <div class="row g-2 mb-2">
          <div class="col-md-4"><label class="pc-label">Total Cold CW (kg)</label><input v-model.number="form.totalColdCwKg" type="number" step="0.1" class="pc-input w-100" @input="recalc" /></div>
          <div class="col-md-4"><label class="pc-label">Price per kg (KES)</label><input v-model.number="form.agreedPricePerKgKes" type="number" step="0.01" class="pc-input w-100" @input="recalc" /></div>
          <div class="col-md-4"><label class="pc-label">Gross carcass (KES)</label>
            <div class="pc-input w-100" style="display:flex;align-items:center;font-weight:700;color:var(--fm-h,#F5F0E8);background:transparent;">KES {{ Number(form.grossCarcassKes||0).toLocaleString() }}</div>
            <div style="font-size:10px;color:var(--fm-s,#6B6356);">Cold CW × price per kg</div>
          </div>
        </div>
        <div class="row g-2 mb-2">
          <div class="col-md-3"><label class="pc-label">Less: Slaughter fee</label><input v-model.number="form.slaughterFeeKes" type="number" class="pc-input w-100" @input="recalc" /></div>
          <div class="col-md-3"><label class="pc-label">Less: Vet inspection fee</label><input v-model.number="form.vetFeeKes" type="number" class="pc-input w-100" @input="recalc" /></div>
          <div class="col-md-3"><label class="pc-label">Less: County levy</label><input v-model.number="form.countyLevyKes" type="number" class="pc-input w-100" @input="recalc" /></div>
          <div class="col-md-3"><label class="pc-label">Less: Discount</label><input v-model.number="form.discountKes" type="number" class="pc-input w-100" @input="recalc" /></div>
          <div class="col-md-3"><label class="pc-label">Other deduction</label>
            <input v-model.number="form.otherDeductionsKes" type="number" class="pc-input w-100" @input="recalc" />
            <input v-model="form.otherDeductionsLabel" class="pc-input w-100 mt-1" placeholder="Label (optional)" style="font-size:11px;" />
          </div>
        </div>
        <div class="pc-subtotal">
          <span>Net carcass payable</span>
          <strong>KES {{ Number(form.netCarcassKes||0).toLocaleString() }}</strong>
        </div>

        <h6 class="fw-bold mt-4 mb-2" style="color:#BFA164;">Offal &amp; by-products</h6>
        <div class="row g-2 mb-2">
          <div class="col-md-3"><label class="pc-label">Total hide value</label><input v-model.number="form.hideValueKes" type="number" class="pc-input w-100" @input="recalc" /></div>
          <div class="col-md-3"><label class="pc-label">Total liver value</label><input v-model.number="form.liverValueKes" type="number" class="pc-input w-100" @input="recalc" /></div>
          <div class="col-md-3"><label class="pc-label">Tripe/rumen value</label><input v-model.number="form.tripeValueKes" type="number" class="pc-input w-100" @input="recalc" /></div>
          <div class="col-md-3"><label class="pc-label">Heads &amp; legs value</label><input v-model.number="form.headsLegsValueKes" type="number" class="pc-input w-100" @input="recalc" /></div>
        </div>
        <div class="row g-2 mb-2">
          <div class="col-md-3"><label class="pc-label">Other offal value</label><input v-model.number="form.otherOffalValueKes" type="number" class="pc-input w-100" @input="recalc" /></div>
        </div>
        <div class="pc-subtotal">
          <span>Total by-products</span>
          <strong>KES {{ Number(form.totalByproductsKes||0).toLocaleString() }}</strong>
        </div>

        <div class="pc-grand">
          <span>GRAND TOTAL PAYABLE</span>
          <strong>KES {{ Number(form.grandTotalKes||0).toLocaleString() }}</strong>
        </div>

        <h6 class="fw-bold mt-4 mb-2" style="color:#BFA164;">Payment routing</h6>
        <div class="row g-2 mb-3">
          <div class="col-md-12"><label class="pc-label">Bank account</label>
            <select v-model.number="form.bankAccountId" class="pc-input w-100">
              <option :value="0">— None —</option>
              <option v-for="b in banks" :key="'ba-'+b.id" :value="b.id">{{ b.account_name }} · {{ b.bank_name }}<span v-if="b.branch_name"> ({{ b.branch_name }})</span> · A/C {{ b.account_no }}</option>
            </select>
            <div v-if="!banks.length" style="color:var(--fm-s,#6B6356);font-size:11px;margin-top:4px;">
              No bank accounts saved. <a href="javascript:void(0)" @click="manageBank=true" style="color:#BFA164;">Add one</a>.
            </div>
          </div>
        </div>

        <div class="mb-3"><label class="pc-label">Notes</label><textarea v-model="form.notes" class="pc-input w-100" rows="2"></textarea></div>

        <div class="d-flex gap-2">
          <button class="btn btn-warning" @click="save" :disabled="saving"><i class="mdi mdi-content-save me-1"></i>{{ form.id ? 'Save changes' : 'Create' }}</button>
          <button class="btn btn-link text-muted" @click="cancelEdit">Cancel</button>
        </div>
      </div>
    </div>

    <!-- BANK ACCOUNTS MODAL -->
    <div v-if="manageBank" class="pc-overlay" @click.self="manageBank=false">
      <div class="pc-modal">
        <div class="d-flex justify-content-between align-items-start mb-3">
          <h5 class="m-0 fw-bold" style="color:var(--fm-h,#F5F0E8);"><i class="mdi mdi-bank me-1"></i>Bank accounts</h5>
          <button class="btn-close btn-close-white" @click="manageBank=false"></button>
        </div>

        <div v-if="!isOwner" style="color:#FF6B6B;font-size:12px;margin-bottom:14px;">
          Only the farm owner can add or edit bank accounts.
        </div>

        <div v-for="b in banks" :key="'b-'+b.id" class="pc-bank-card">
          <div class="d-flex justify-content-between align-items-start">
            <div>
              <div style="color:var(--fm-h,#F5F0E8);font-weight:700;">{{ b.account_name }} <span v-if="b.is_primary == 1" class="pc-primary-pill">Primary</span></div>
              <div style="color:var(--fm-m,#A09888);font-size:12px;">{{ b.bank_name }} <span v-if="b.branch_name">· {{ b.branch_name }}</span></div>
              <div style="color:var(--fm-s,#6B6356);font-size:11px;font-family:monospace;">A/C {{ b.account_no }}<span v-if="b.branch_code"> · BC {{ b.branch_code }}</span><span v-if="b.swift_code"> · SWIFT {{ b.swift_code }}</span></div>
            </div>
            <div v-if="isOwner" class="d-flex gap-1">
              <button class="btn btn-sm btn-outline-warning" @click="editBank(b)"><i class="mdi mdi-pencil"></i></button>
              <button class="btn btn-sm btn-outline-danger" @click="delBank(b)"><i class="mdi mdi-delete"></i></button>
            </div>
          </div>
        </div>
        <div v-if="!banks.length" style="color:var(--fm-s,#6B6356);font-size:12px;text-align:center;padding:14px;">No bank accounts yet.</div>

        <div v-if="isOwner && showBankForm" class="pc-card mt-3" style="background:rgba(191,161,100,.05);">
          <h6 class="fw-bold mb-2" style="color:var(--fm-h,#F5F0E8);">{{ bankForm.id ? 'Edit bank account' : 'Add bank account' }}</h6>
          <div class="row g-2 mb-2">
            <div class="col-md-7"><label class="pc-label">Account name *</label><input v-model="bankForm.accountName" class="pc-input w-100" placeholder="e.g. RANCH EXPERTS LTD" /></div>
            <div class="col-md-5"><label class="pc-label">Bank name *</label><input v-model="bankForm.bankName" class="pc-input w-100" placeholder="KCB BANK LTD" /></div>
          </div>
          <div class="row g-2 mb-2">
            <div class="col-md-6"><label class="pc-label">Branch</label><input v-model="bankForm.branchName" class="pc-input w-100" placeholder="NANYUKI BRANCH" /></div>
            <div class="col-md-6"><label class="pc-label">Account number *</label><input v-model="bankForm.accountNo" class="pc-input w-100" placeholder="1331930715" /></div>
          </div>
          <div class="row g-2 mb-2">
            <div class="col-md-4"><label class="pc-label">Branch code</label><input v-model="bankForm.branchCode" class="pc-input w-100" placeholder="029" /></div>
            <div class="col-md-4"><label class="pc-label">SWIFT code</label><input v-model="bankForm.swiftCode" class="pc-input w-100" placeholder="KCBLKENX" /></div>
            <div class="col-md-4 d-flex align-items-end">
              <label class="pc-chk"><input type="checkbox" v-model="bankForm.isPrimary" /> Primary</label>
            </div>
          </div>
          <div class="d-flex gap-2">
            <button class="btn btn-warning btn-sm" @click="saveBank"><i class="mdi mdi-content-save me-1"></i>Save</button>
            <button class="btn btn-link text-muted btn-sm" @click="showBankForm=false">Cancel</button>
          </div>
        </div>
        <button v-if="isOwner && !showBankForm" class="btn btn-outline-warning w-100 mt-2" @click="newBank"><i class="mdi mdi-plus me-1"></i>Add bank account</button>
      </div>
    </div>
  </div>
</div>
</template>

<style>
:root{--fm-h:#F5F0E8;--fm-s:#6B6356;--fm-m:#A09888;--fm-bg1:#141414;--fm-bg2:#0F0F0F;--fm-bg3:#1E1E1E;--fm-br:#1E1E1E}
html[data-theme="light"]{--fm-h:#1A1A1A;--fm-s:#777;--fm-m:#555;--fm-bg1:#FFFFFF;--fm-bg2:#F7F5F2;--fm-bg3:#F0EBE4;--fm-br:#E8E2DA}
html[data-theme="light"] .pc-root{background:#F7F5F2!important;color:#1A1A1A!important}
html[data-theme="light"] .pc-topbar{background:rgba(255,255,255,.95)!important;border-bottom-color:#E8E2DA!important}
html[data-theme="light"] .pc-card,html[data-theme="light"] .pc-modal,html[data-theme="light"] .pc-bank-card{background:#FFFFFF!important;border-color:#E8E2DA!important}
html[data-theme="light"] .pc-input{background:#F7F5F2!important;border-color:#E0D8D0!important;color:#1A1A1A!important}
</style>

<style scoped>
.pc-root{background:var(--fm-bg2,#0A0A0A);min-height:100vh;color:var(--fm-h,#F5F0E8)}
.pc-topbar{position:sticky;top:0;z-index:100;padding:12px 0;background:var(--fm-bg1,#141414);backdrop-filter:blur(10px);border-bottom:1px solid var(--fm-br,#1E1E1E)}
.pc-container{padding-top:80px;padding-bottom:60px;max-width:980px}
.pc-card{background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:12px;padding:18px}
.pc-bank-card{background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:10px;padding:12px;margin-bottom:8px}
.pc-ref{font-family:monospace;color:#BFA164;font-weight:700;font-size:13px;background:rgba(191,161,100,.1);padding:3px 8px;border-radius:6px}
.pc-status{font-size:10px;font-weight:700;padding:3px 10px;border-radius:20px;text-transform:uppercase;letter-spacing:.4px}
.pc-status.st-draft{background:rgba(160,152,136,.16);color:#A09888}
.pc-status.st-calculated{background:rgba(102,178,255,.16);color:#66B2FF}
.pc-status.st-approved{background:rgba(80,200,120,.16);color:#50C878}
.pc-status.st-paid{background:rgba(191,161,100,.16);color:#BFA164}
.pc-label{display:block;font-size:11px;color:var(--fm-s);text-transform:uppercase;letter-spacing:.3px;margin-bottom:4px;font-weight:600}
.pc-input{background:var(--fm-bg2,#0F0F0F);border:1px solid var(--fm-br,#2A2520);border-radius:8px;padding:8px 10px;color:var(--fm-h,#F5F0E8);font-size:13px}
.pc-input:focus{outline:none;border-color:#BFA164}
.pc-chk{font-size:12px;color:var(--fm-h);cursor:pointer;display:flex;align-items:center;gap:6px}
.pc-subtotal{display:flex;justify-content:space-between;align-items:center;padding:10px 14px;background:rgba(191,161,100,.06);border:1px solid rgba(191,161,100,.2);border-radius:8px;margin-top:8px;font-size:13px;color:var(--fm-h,#F5F0E8)}
.pc-grand{display:flex;justify-content:space-between;align-items:center;padding:14px 18px;background:rgba(80,200,120,.12);border:2px solid #50C878;border-radius:10px;margin-top:14px;font-size:16px;color:var(--fm-h,#F5F0E8);font-weight:700}
.pc-primary-pill{font-size:10px;font-weight:700;padding:1px 8px;border-radius:8px;background:rgba(80,200,120,.16);color:#50C878;margin-left:6px}
.pc-overlay{position:fixed;inset:0;background:rgba(0,0,0,.6);display:flex;align-items:flex-start;justify-content:center;padding:30px 16px;z-index:2000;overflow-y:auto}
.pc-modal{background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:14px;padding:20px;max-width:640px;width:100%}

/* ===== MOBILE — feels-like-an-app ===== */
@media (max-width: 768px) {
  .pc-container { padding-top: 60px !important; padding-bottom: calc(80px + env(safe-area-inset-bottom, 0px)) !important; padding-left: 12px !important; padding-right: 12px !important; }
  .pc-card { padding: 12px !important; border-radius: 10px !important; }
  .pc-grand { padding: 10px 12px; font-size: 14px; }
  .pc-overlay { padding: 14px; align-items: flex-end; }
  .pc-modal { padding: 14px; border-radius: 14px 14px 0 0; max-height: 90vh; overflow-y: auto; }
}
</style>

<script>
/* eslint-disable */
import { executeJson, flocker } from "@/api";
export default {
  name: "PaymentCalculations",
  data(){return{
    darkMode:(document.documentElement.getAttribute('data-theme')||localStorage.getItem('flockr_theme')||'dark')!=='light',
    themeObserver:null,
    loading:true, saving:false,
    farm:{}, calcs:[], signedSheets:[], banks:[],
    isOwner:false,
    editing:false, manageBank:false, showBankForm:false,
    form:this._blankForm(),
    bankForm:{ id:0, accountName:'', bankName:'', branchName:'', accountNo:'', branchCode:'', swiftCode:'', isPrimary:false }
  }},
  computed:{ themePageStyle(){ return this.darkMode ? 'background:#0A0A0A;min-height:100vh;color:#F5F0E8;' : 'background:#F7F5F2;min-height:100vh;color:#1A1A1A;'; }, farmId(){ return this.$route.params.farmId; } },
  mounted(){
    this._applyThemeOnce();var self=this;
    this.themeObserver=new MutationObserver(function(){try{self._applyThemeOnce();}catch(e){}});
    this.themeObserver.observe(document.documentElement,{attributes:true,attributeFilter:['data-theme']});
    if(!sessionStorage.getItem('userId')){this.$router.push('/login');return}
    this.loadAll();
  },
  beforeUnmount(){ if(this.themeObserver){this.themeObserver.disconnect()} },
  methods:{
    _applyThemeOnce(){var current=document.documentElement.getAttribute('data-theme');var desired=(localStorage.getItem('flockr_theme')||current||'dark');if(current!==desired)document.documentElement.setAttribute('data-theme',desired);this.darkMode=desired!=='light';},
    _blankForm(){
      return { id:0, slaughterSheetId:0, buyerName:'', calcDate:new Date().toISOString().slice(0,10),
        totalColdCwKg:null, agreedPricePerKgKes:null, grossCarcassKes:null,
        slaughterFeeKes:0, vetFeeKes:0, countyLevyKes:0, otherDeductionsKes:0, otherDeductionsLabel:'', discountKes:0,
        netCarcassKes:0,
        hideValueKes:0, liverValueKes:0, tripeValueKes:0, headsLegsValueKes:0, otherOffalValueKes:0,
        totalByproductsKes:0, grandTotalKes:0,
        bankAccountId:0, notes:'' };
    },
    fD(d){ if(!d)return''; try{ return new Date(d).toLocaleDateString(undefined,{day:'2-digit',month:'short',year:'numeric'}) }catch(e){return d} },
    statusLabel(s){ return ({draft:'Draft',calculated:'Calculated',approved:'Approved',paid:'Paid'})[s] || s; },
    async loadAll(){
      this.loading = true;
      try {
        var uid = sessionStorage.getItem('userId') || '';
        var f = await executeJson({ function:'getFarmDetail', farmId:this.farmId }, flocker);
        if (f.data && f.data.success) {
          this.farm = (f.data.data && f.data.data.farm) || {};
          this.isOwner = (String(this.farm.owner_id) === String(uid));
        }
        var pc = await executeJson({ function:'listPaymentCalculations', farmId:this.farmId }, flocker);
        if (pc.data && pc.data.success) this.calcs = (pc.data.data && pc.data.data.paymentCalculations) || [];
        var ss = await executeJson({ function:'listSlaughterSheets', farmId:this.farmId }, flocker);
        if (ss.data && ss.data.success) {
          this.signedSheets = ((ss.data.data && ss.data.data.slaughterSheets) || []).filter(function(x){ return x.status === 'signed_off' || x.status === 'post_mortem_complete'; });
        }
        var b = await executeJson({ function:'listFarmBankAccounts', farmId:this.farmId }, flocker);
        if (b.data && b.data.success) this.banks = (b.data.data && b.data.data.accounts) || [];
      } catch(e){ console.error(e); }
      this.loading = false;
    },
    openCreate(){ this.form = this._blankForm(); this.editing = true; },
    async openEdit(c){
      try {
        var r = await executeJson({ function:'getPaymentCalculation', paymentCalcId:c.id }, flocker);
        if (r.data && r.data.success) {
          var p = r.data.data.paymentCalculation;
          this.form = {
            id: p.id, slaughterSheetId: p.slaughter_sheet_id ? Number(p.slaughter_sheet_id) : 0,
            buyerName: p.buyer_name || '', calcDate: p.calc_date || new Date().toISOString().slice(0,10),
            totalColdCwKg: p.total_cold_cw_kg ? Number(p.total_cold_cw_kg) : null,
            agreedPricePerKgKes: p.agreed_price_per_kg_kes ? Number(p.agreed_price_per_kg_kes) : null,
            grossCarcassKes: p.gross_carcass_kes ? Number(p.gross_carcass_kes) : null,
            slaughterFeeKes: Number(p.slaughter_fee_kes||0), vetFeeKes: Number(p.vet_fee_kes||0),
            countyLevyKes: Number(p.county_levy_kes||0), otherDeductionsKes: Number(p.other_deductions_kes||0),
            otherDeductionsLabel: p.other_deductions_label || '', discountKes: Number(p.discount_kes||0),
            netCarcassKes: Number(p.net_carcass_kes||0),
            hideValueKes: Number(p.hide_value_kes||0), liverValueKes: Number(p.liver_value_kes||0),
            tripeValueKes: Number(p.tripe_value_kes||0), headsLegsValueKes: Number(p.heads_legs_value_kes||0),
            otherOffalValueKes: Number(p.other_offal_value_kes||0),
            totalByproductsKes: Number(p.total_byproducts_kes||0),
            grandTotalKes: Number(p.grand_total_kes||0),
            bankAccountId: p.bank_account_id ? Number(p.bank_account_id) : 0,
            notes: p.notes || ''
          };
          this.editing = true;
        }
      } catch(e){ alert('Could not load.') }
    },
    cancelEdit(){ this.editing = false; this.loadAll(); },
    async onSheetPicked(){
      var id = this.form.slaughterSheetId;
      if (!id) return;
      var picked = this.signedSheets.find(function(s){ return s.id == id; });
      if (!picked) return;
      // Fetch full sheet to get totals
      try {
        var r = await executeJson({ function:'getSlaughterSheet', slaughterSheetId:id }, flocker);
        if (r.data && r.data.success) {
          var sheet = r.data.data.slaughterSheet;
          var totalCw = 0;
          (sheet.items || []).forEach(function(it){ totalCw += Number(it.cold_cw_kg) || 0; });
          this.form.totalColdCwKg = Math.round(totalCw * 100) / 100;
          // If any animal has a price_per_kg captured, use the first non-zero as default
          var firstPrice = (sheet.items || []).map(function(it){ return Number(it.price_per_kg_kes); }).filter(function(v){return v>0;})[0];
          if (firstPrice && !this.form.agreedPricePerKgKes) this.form.agreedPricePerKgKes = firstPrice;
          this.recalc();
        }
      } catch(e){}
    },
    recalc(){
      var gross = Math.round((Number(this.form.totalColdCwKg) || 0) * (Number(this.form.agreedPricePerKgKes) || 0) * 100) / 100;
      this.form.grossCarcassKes = gross;
      var deductions = (Number(this.form.slaughterFeeKes)||0) + (Number(this.form.vetFeeKes)||0) + (Number(this.form.countyLevyKes)||0) + (Number(this.form.otherDeductionsKes)||0) + (Number(this.form.discountKes)||0);
      this.form.netCarcassKes = Math.round((gross - deductions) * 100) / 100;
      this.form.totalByproductsKes = Math.round(((Number(this.form.hideValueKes)||0) + (Number(this.form.liverValueKes)||0) + (Number(this.form.tripeValueKes)||0) + (Number(this.form.headsLegsValueKes)||0) + (Number(this.form.otherOffalValueKes)||0)) * 100) / 100;
      this.form.grandTotalKes = Math.round((this.form.netCarcassKes + this.form.totalByproductsKes) * 100) / 100;
    },
    async save(){
      this.saving = true;
      this.recalc();
      try {
        var p = Object.assign({ function:'savePaymentCalculation', farmId:this.farmId, userId:sessionStorage.getItem('userId') }, this.form);
        if (!p.id) delete p.id;
        var r = await executeJson(p, flocker);
        if (r.data && r.data.success) { this.cancelEdit(); }
        else alert((r.data && r.data.message) || 'Failed');
      } catch(e){ alert('Network error'); }
      this.saving = false;
    },
    async approve(c){
      if (!confirm('Approve "' + c.document_ref + '"?')) return;
      try {
        var r = await executeJson({ function:'approvePaymentCalculation', paymentCalcId:c.id, userId:sessionStorage.getItem('userId') }, flocker);
        if (r.data && r.data.success) await this.loadAll();
        else alert((r.data && r.data.message) || 'Failed');
      } catch(e){ alert('Network error') }
    },
    async markPaid(c){
      var ref = prompt('RTGS / payment reference (optional):');
      if (ref === null) return;
      try {
        var r = await executeJson({ function:'markPaymentCalculationPaid', paymentCalcId:c.id, userId:sessionStorage.getItem('userId'), paymentMethod:'RTGS', rtgsRef: ref || '' }, flocker);
        if (r.data && r.data.success) await this.loadAll();
        else alert((r.data && r.data.message) || 'Failed');
      } catch(e){ alert('Network error') }
    },
    openPrint(c){
      var route = this.$router.resolve({ path: '/payment-calc/' + c.id + '/print' });
      window.open(route.href, '_blank');
    },
    newBank(){ this.bankForm = { id:0, accountName:'', bankName:'', branchName:'', accountNo:'', branchCode:'', swiftCode:'', isPrimary:!this.banks.length }; this.showBankForm = true; },
    editBank(b){ this.bankForm = { id:b.id, accountName:b.account_name, bankName:b.bank_name, branchName:b.branch_name||'', accountNo:b.account_no, branchCode:b.branch_code||'', swiftCode:b.swift_code||'', isPrimary: b.is_primary == 1 }; this.showBankForm = true; },
    async saveBank(){
      if (!this.bankForm.accountName || !this.bankForm.bankName || !this.bankForm.accountNo) { alert('Account name, bank and account number required'); return; }
      try {
        var p = Object.assign({ function:'saveFarmBankAccount', farmId:this.farmId, userId:sessionStorage.getItem('userId'), isPrimary: this.bankForm.isPrimary ? 1 : 0 }, this.bankForm);
        if (!p.id) delete p.id;
        var r = await executeJson(p, flocker);
        if (r.data && r.data.success) { this.showBankForm = false; await this.loadAll(); }
        else alert((r.data && r.data.message) || 'Failed');
      } catch(e){ alert('Network error') }
    },
    async delBank(b){
      if (!confirm('Remove bank account "' + b.account_name + '"?')) return;
      try {
        var r = await executeJson({ function:'deleteFarmBankAccount', accountId:b.id, farmId:this.farmId, userId:sessionStorage.getItem('userId') }, flocker);
        if (r.data && r.data.success) await this.loadAll();
      } catch(e){ alert('Network error') }
    }
  }
};
</script>
